import { type Vec2, vec } from '../math';

import { type Euler, rotate, type Vec3, vec3 } from './Vec3';

/**
 * Projections from three dimensions to the drawing plane.
 *
 * All three the brief asks for, and they divide into two families that behave differently
 * enough to be worth naming:
 *
 * - **Isometric** and **cabinet** are *parallel* projections. Parallel lines stay parallel,
 *   and whether a face points towards the viewer is a property of the face alone.
 * - **Perspective** is not. Whether a face is visible depends on where it sits relative to
 *   the eye, so the visibility test has to know the eye position.
 *
 * That difference is the reason `Projector` exposes `isFrontFacing` rather than leaving
 * callers to check the sign of a normal: the correct test is not the same for all three,
 * and a caller that assumed otherwise would produce a drawing whose hidden lines are subtly
 * wrong only under perspective.
 */

export const ProjectionKind = {
  Isometric: 'isometric',
  Cabinet: 'cabinet',
  Perspective: 'perspective',
} as const;
export type ProjectionKind = (typeof ProjectionKind)[keyof typeof ProjectionKind];

export interface Projector {
  /** World space to view space, with the solid's own rotation already applied. */
  toView(point: Vec3): Vec3;
  /** View space to the drawing plane. */
  toPlane(view: Vec3): Vec2;
  /** Whether a face with this outward normal, at this centroid, faces the viewer. */
  isFrontFacing(normalInView: Vec3, centroidInView: Vec3): boolean;
}

/**
 * The isometric viewing rotation.
 *
 * Isometric means looking down the (1, 1, 1) diagonal, so that the three principal axes are
 * equally foreshortened and project 120° apart. Bringing that diagonal onto the view axis
 * takes −45° about the upright axis followed by `atan(1/√2)` ≈ 35.264° about the horizontal.
 *
 * **The order is not interchangeable**, and the two rotations are applied as two separate
 * calls for exactly that reason: `rotate` composes X before Y, so passing both angles at
 * once would yaw *after* pitching and land the depth axis 60° from where it belongs. The
 * tests assert the 120° property rather than these constants, which is how that was caught.
 */
const ISOMETRIC_YAW = -Math.PI / 4;
const ISOMETRIC_PITCH = Math.atan(1 / Math.SQRT2);

/**
 * Cabinet projection halves the depth axis and lays it at 30°.
 *
 * The axis is the one **receding from the reader** — the depth runs away up and to the right,
 * so the near face sits low and left and the visible faces are the front, the top and the
 * right. That is what every drafting text and every school figure means by cabinet.
 *
 * It used to be `+z`, the axis coming *towards* the reader, which drew the same solid as if
 * seen from below and behind. Nothing complained, because the two differ only in which lines
 * are dashed and the classifier was not deciding that correctly either — see `parallel` below.
 *
 * ## Why 30° and not the 45° this started with
 *
 * Because at 45° the receding edge of a cube is **exactly parallel to the diagonal of its own
 * front face**, and that face is a square precisely when the solid is a cube. Measured on the
 * corpus: 0.0° of separation. Every problem that draws a face diagonal — `AC'`, `BD'`, `AB'`,
 * which is a large share of them — then produces a line indistinguishable in direction from the
 * depth edges, and where the two share a vertex they lie on top of one another.
 *
 * That is the degeneracy oblique projection has to avoid, and 45° is the single worst angle for
 * it: the front-face diagonal and the depth edge point exactly the same way.
 *
 * ## Why 60° and not the 30° this used to be
 *
 * Because the first sweep only ran from 20° to 45°, and the two things it measured are symmetric
 * about 45°. Reflecting a candidate across that line leaves both of them untouched and changes
 * the third thing, which was not being measured:
 *
 * ```
 *   depth angle   worst corner separation   diagonal vs depth   square base, open
 *        30°              30°                     15°                  17%
 *        45°              45°                      0°                  26%
 *        60°              30°                     15°                  35%
 * ```
 *
 * So 60° is not a trade against 30°; it is the same drawing seen from higher up. Both angular
 * measures are identical and the base is twice as open.
 *
 * That third column is what a reader actually complains about. At 17% a square base is drawn six
 * times wider than it is deep — a sliver, with the far corner sitting almost on the near edge —
 * and a figure of "đáy ABCD là hình vuông tâm O" had O and D piled onto AB. At 35% the base is a
 * parallelogram anyone can see is meant to be a square. The same number is the ry/rx of a cone's
 * base ellipse, so round solids stop looking pressed flat as well.
 *
 * The corpus is the check that this is not merely arithmetic: `noAmbiguousCorner` runs over every
 * figure in it and is what says the corners stayed apart.
 *
 * The foreshortening, not the angle, is what makes this *cabinet*: halving the depth is the
 * defining property, and 30°, 45° and 60° are all conventional choices of direction.
 */
const CABINET_ANGLE = Math.PI / 3;
const CABINET_SCALE = 0.5;
const CABINET_X = Math.cos(CABINET_ANGLE) * CABINET_SCALE;
const CABINET_Y = Math.sin(CABINET_ANGLE) * CABINET_SCALE;

/** Eye distance for a solid small enough not to need the camera pulled back. */
export const DEFAULT_EYE_DISTANCE = 900;

/**
 * How many bounding radii the eye is kept away from the solid's centre, at minimum.
 *
 * Three is far enough that the near face is never more than twice the far face, which keeps
 * the drawing readable, and comfortably clear of the singularity at one radius.
 */
const MIN_EYE_RADII = 3;

/**
 * The eye distance to use for a solid of the given bounding radius.
 *
 * A fixed eye distance is fine until the solid grows to reach it, and then it stops being
 * a projection at all. Measured on the fixed 900: a cube 1800 units across put its front
 * face *level with the eye*, where the projection divides by zero. Clamping the divisor
 * bounded the damage without fixing it — the front face came out twenty times oversized and
 * every face classified as pointing away, so the whole solid drew as dashed lines twenty
 * times too big.
 *
 * Scaling the eye distance with the solid removes the failure rather than capping it: the
 * eye is always outside, so no vertex can reach or pass it and no clamp is needed. It is
 * also what a photographer does — you step back to fit the subject in frame, and the
 * perspective softens as you go. Small solids are unaffected, because the fixed distance
 * still dominates.
 */
export function eyeDistanceFor(boundingRadius: number): number {
  return Math.max(DEFAULT_EYE_DISTANCE, boundingRadius * MIN_EYE_RADII);
}

export function createProjector(
  kind: ProjectionKind,
  rotation: Euler,
  eyeDistance: number = DEFAULT_EYE_DISTANCE
): Projector {
  switch (kind) {
    case ProjectionKind.Isometric:
      return parallel(kind, rotation);
    case ProjectionKind.Cabinet:
      return parallel(kind, rotation);
    case ProjectionKind.Perspective:
      return perspective(rotation, eyeDistance);
  }
}

function parallel(kind: ProjectionKind, rotation: Euler): Projector {
  const isometric = kind === ProjectionKind.Isometric;

  return {
    toView(point) {
      const rotated = rotate(point, rotation);
      /*
       * Cabinet keeps the front face true, so it applies no viewing rotation of its own —
       * the shear happens on the way to the plane. Isometric applies its fixed viewing
       * rotation on top of whatever the user has turned the solid to.
       */
      if (!isometric) return rotated;
      const yawed = rotate(rotated, { x: 0, y: ISOMETRIC_YAW, z: 0 });
      return rotate(yawed, { x: ISOMETRIC_PITCH, y: 0, z: 0 });
    },

    toPlane(view) {
      if (isometric) return vec.vec2(view.x, view.y);
      // Negated, so the axis drawn up-and-right is the one going *away*. See CABINET_ANGLE.
      return vec.vec2(view.x - view.z * CABINET_X, view.y - view.z * CABINET_Y);
    },

    /**
     * Whether a face points towards the reader.
     *
     * ## Isometric: look along `z`
     *
     * A true orthographic view, taken after the viewing rotation has been applied. The viewer
     * is infinitely far along `+z` and only the normal matters.
     *
     * ## Cabinet: look along the axis the shear collapses
     *
     * An oblique projection does **not** look along `z`. Two points differing by `(cos, sin, 1)`
     * land on the same spot on the page, so that — not `z` — is the line of sight, and facing
     * has to be measured against it.
     *
     * Testing `normal.z > 0` here was wrong, and wrong in the way that hurts most: for an
     * axis-aligned box every side face is *exactly* edge-on to `z`, so all four scored zero,
     * all four classified as pointing away, and a cube came out as **four solid lines and eight
     * dashes**. Not subtly off — unusable, and only for the users who chose cabinet, which is
     * why it survived. The default is isometric, where the old test happened to be correct.
     */
    isFrontFacing(normalInView) {
      if (isometric) return normalInView.z > 0;
      return normalInView.x * CABINET_X + normalInView.y * CABINET_Y + normalInView.z > 0;
    },
  };
}

function perspective(rotation: Euler, eyeDistance: number): Projector {
  const eye = vec3(0, 0, eyeDistance);

  return {
    toView(point) {
      return rotate(point, rotation);
    },

    toPlane(view) {
      /*
       * The clamp is a backstop, not the mechanism. `eyeDistanceFor` keeps the eye outside
       * the solid, so a caller that goes through `projectMesh` can never reach it. It
       * remains for a caller that builds a projector directly with an eye distance of its
       * own choosing, where returning a coordinate at infinity would take the hit-test
       * bounds and the dirty rectangle with it.
       */
      const depth = Math.max(eyeDistance - view.z, eyeDistance * 0.05);
      const k = eyeDistance / depth;
      return vec.vec2(view.x * k, view.y * k);
    },

    isFrontFacing(normalInView, centroidInView) {
      // Under perspective the answer depends on where the face is, not only on its normal.
      const toEye = {
        x: eye.x - centroidInView.x,
        y: eye.y - centroidInView.y,
        z: eye.z - centroidInView.z,
      };
      return normalInView.x * toEye.x + normalInView.y * toEye.y + normalInView.z * toEye.z > 0;
    },
  };
}
